/**
 * Stripe Service
 * Credits-based billing with customer.metadata storage
 * Note: Currently in demo mode (localStorage). When backend is added,
 * this will use Stripe customer.metadata for credit storage.
 */

import { useStore } from '@store/useStore'

// Credit packs pricing (1 site ≈ 15 credits)
export const CREDIT_PACKS = [
  {
    id: 'pack_1',
    credits: 15,
    price: 1000,
    label: '1 site',
    priceLabel: '10€',
    bonus: null,
  },
  {
    id: 'pack_5',
    credits: 75,
    price: 4000,
    label: '5 sites',
    priceLabel: '40€',
    bonus: '1 gratuit !',
    popular: true,
  },
  {
    id: 'pack_10',
    credits: 150,
    price: 7000,
    label: '10 sites',
    priceLabel: '70€',
    bonus: '3 gratuits !',
  },
  {
    id: 'pack_25',
    credits: 375,
    price: 15000,
    label: '25 sites',
    priceLabel: '150€',
    bonus: '10 gratuits !',
    bestValue: true,
  },
]

// Promo codes database
// In production, this would be stored in Stripe or a backend
export const PROMO_CODES = {
  // Christmas 2024 - 20 bonus credits
  'NOEL2024': {
    type: 'credits',
    value: 20,
    description: '20 crédits offerts pour Noël !',
    expiresAt: '2025-01-05',
    maxUses: null, // unlimited
    icon: 'gift',
  },
  // New Year bonus
  'BONNE2025': {
    type: 'credits',
    value: 15,
    description: '15 crédits pour bien démarrer 2025 !',
    expiresAt: '2025-01-31',
    maxUses: null,
    icon: 'sparkles',
  },
  // Welcome bonus - 10 extra credits
  'BIENVENUE': {
    type: 'credits',
    value: 10,
    description: '10 crédits de bienvenue !',
    expiresAt: '2025-12-31',
    maxUses: 1, // per user
    icon: 'rocket',
  },
  // Affiliate/partner codes
  'KOEKI50': {
    type: 'credits',
    value: 50,
    description: '50 crédits partenaire Koeki !',
    expiresAt: '2025-06-30',
    maxUses: 1,
    icon: 'zap',
  },
  // Beta tester reward
  'BETATEST': {
    type: 'credits',
    value: 100,
    description: '100 crédits beta testeur !',
    expiresAt: '2025-03-31',
    maxUses: 1,
    icon: 'award',
  },
  // Development mode - unlimited refills
  'DEVMODE': {
    type: 'credits',
    value: 100,
    description: '100 crédits dev mode !',
    expiresAt: '2099-12-31',
    maxUses: null, // unlimited uses for dev
    icon: 'code',
  },
  // Quick refill
  'REFILL50': {
    type: 'credits',
    value: 50,
    description: '50 crédits de recharge !',
    expiresAt: '2099-12-31',
    maxUses: null,
    icon: 'battery-charging',
  },
}

/**
 * Get used promo codes for current user
 */
export function getUsedPromoCodes() {
  return JSON.parse(localStorage.getItem('emd_used_promos') || '[]')
}

/**
 * Mark a promo code as used
 */
function markPromoCodeUsed(code) {
  const used = getUsedPromoCodes()
  used.push({
    code: code.toUpperCase(),
    usedAt: new Date().toISOString(),
  })
  localStorage.setItem('emd_used_promos', JSON.stringify(used))
}

/**
 * Validate and apply a promo code
 * @param {string} code - The promo code to apply
 * @returns {Object} Result with success status and message
 */
export function applyPromoCode(code) {
  const normalizedCode = code.trim().toUpperCase()
  const promo = PROMO_CODES[normalizedCode]

  // Check if code exists
  if (!promo) {
    return {
      success: false,
      message: 'Code promo invalide',
    }
  }

  // Check expiration
  if (promo.expiresAt && new Date(promo.expiresAt) < new Date()) {
    return {
      success: false,
      message: 'Ce code promo a expiré',
    }
  }

  // Check if already used (for one-time codes)
  const usedCodes = getUsedPromoCodes()
  const alreadyUsed = usedCodes.some(u => u.code === normalizedCode)
  if (promo.maxUses === 1 && alreadyUsed) {
    return {
      success: false,
      message: 'Vous avez déjà utilisé ce code',
    }
  }

  // Apply the promo based on type
  if (promo.type === 'credits') {
    const current = getCredits()
    setCredits(current + promo.value)
    markPromoCodeUsed(normalizedCode)

    return {
      success: true,
      message: promo.description,
      credits: promo.value,
      newBalance: current + promo.value,
      icon: promo.icon,
    }
  }

  return {
    success: false,
    message: 'Type de code non supporté',
  }
}

/**
 * Get active promo codes (for display)
 */
export function getActivePromoCodes() {
  const now = new Date()
  return Object.entries(PROMO_CODES)
    .filter(([_, promo]) => !promo.expiresAt || new Date(promo.expiresAt) >= now)
    .map(([code, promo]) => ({
      code,
      ...promo,
    }))
}

// Credit costs per operation
export const CREDIT_COSTS = {
  serpAnalysis: 1,      // Web search SERP analysis
  agentPhase: 1,        // Each GPT agent phase
  pageGeneration: 1,    // Generate one HTML page
  imageGeneration: 1,   // Generate one image
  fullSite: 15,         // Estimate for complete site
}

/**
 * Get Stripe public key
 */
function getPublicKey() {
  // In production, this would come from environment
  return localStorage.getItem('emd_stripe_pk') || null
}

/**
 * Get current user credits from localStorage (demo mode)
 * In production, this would fetch from Stripe customer.metadata
 */
export function getCredits() {
  const stored = localStorage.getItem('emd_credits')
  return stored ? parseInt(stored, 10) : 0
}

/**
 * Set credits (demo mode)
 * Also syncs with Zustand store for reactivity
 */
export function setCredits(amount) {
  localStorage.setItem('emd_credits', amount.toString())
  // Sync with Zustand store if available
  try {
    useStore.getState().setCredits(amount)
  } catch (e) {
    // Store might not be initialized yet
  }
}

/**
 * Deduct credits for an operation
 * @param {string} operation - Operation type from CREDIT_COSTS
 * @param {number} quantity - Number of operations (default 1)
 * @returns {boolean} Success
 */
export function deductCredits(operation, quantity = 1) {
  const cost = CREDIT_COSTS[operation] * quantity
  const current = getCredits()

  if (current < cost) {
    return false
  }

  setCredits(current - cost)

  // Log usage
  logUsage(operation, cost)

  return true
}

/**
 * Check if user has enough credits
 */
export function hasCredits(operation, quantity = 1) {
  const cost = CREDIT_COSTS[operation] * quantity
  return getCredits() >= cost
}

/**
 * Estimate total credits needed for a site
 * @param {number} pageCount - Number of pages
 * @param {number} imageCount - Number of images
 * @returns {Object} Credit breakdown
 */
export function estimateSiteCost(pageCount = 10, imageCount = 15) {
  const breakdown = {
    serpAnalysis: CREDIT_COSTS.serpAnalysis,
    agents: CREDIT_COSTS.agentPhase * 6, // 6 pipeline phases
    pages: CREDIT_COSTS.pageGeneration * pageCount,
    images: CREDIT_COSTS.imageGeneration * imageCount,
  }

  breakdown.total = Object.values(breakdown).reduce((a, b) => a + b, 0)

  return breakdown
}

/**
 * Log credit usage for analytics
 */
function logUsage(operation, amount) {
  const usage = JSON.parse(localStorage.getItem('emd_usage') || '[]')
  usage.push({
    operation,
    amount,
    timestamp: new Date().toISOString(),
  })
  // Keep last 100 entries
  if (usage.length > 100) usage.shift()
  localStorage.setItem('emd_usage', JSON.stringify(usage))
}

/**
 * Get usage history
 */
export function getUsageHistory() {
  return JSON.parse(localStorage.getItem('emd_usage') || '[]')
}

/**
 * Create Stripe checkout session (would call backend in production)
 * Demo mode: just add credits
 */
export async function purchaseCredits(packId) {
  const pack = CREDIT_PACKS.find(p => p.id === packId)
  if (!pack) {
    throw new Error('Invalid pack')
  }

  // Demo mode: instantly add credits
  const current = getCredits()
  setCredits(current + pack.credits)

  return {
    success: true,
    credits: pack.credits,
    newBalance: current + pack.credits,
  }
}

/**
 * Initialize with demo credits (for testing)
 */
export function initDemoCredits() {
  if (getCredits() === 0) {
    setCredits(50) // Start with 50 demo credits
  }
}

export default {
  CREDIT_PACKS,
  CREDIT_COSTS,
  PROMO_CODES,
  getCredits,
  setCredits,
  deductCredits,
  hasCredits,
  estimateSiteCost,
  getUsageHistory,
  purchaseCredits,
  initDemoCredits,
  applyPromoCode,
  getUsedPromoCodes,
  getActivePromoCodes,
}
